import type { CSSProperties } from 'react';

import { whiteAlpha } from '@/config/appColors';
import { lightenColor } from '@/utils/extensions/colorExtension';

import styles from './PrimaryButton.module.css';

/**
 * Перенос `lib/src/presentation/widgets/primary_button.dart`.
 *
 * Геометрия, цвета и текстовый стиль живут в CSS-модуле; здесь остаётся
 * только то, что в Dart считалось кодом.
 */

/** `Colors.white.withValues(alpha: 0.05)` */
const BACKGROUND_COLOR = whiteAlpha(0.05);

/** `overlayColor: backgroundColor.lightenColor(0.9)` */
const OVERLAY_COLOR = lightenColor(BACKGROUND_COLOR, 0.9);

const COLOR_STYLE = {
  '--background-color': BACKGROUND_COLOR,
  '--overlay-color': OVERLAY_COLOR,
} as CSSProperties;

export interface PrimaryButtonProps {
  readonly title: string;
  /** Число — пиксели, строка — готовое CSS-значение, например `wv(320)`. */
  readonly width?: number | string;
  readonly height?: number | string;
  /** `onPressed == null` — кнопка недоступна, как у ElevatedButton. */
  readonly onPressed?: (() => void) | null;
}

function cssSize(value: number | string | undefined): string | undefined {
  if (value === undefined) return undefined;
  return typeof value === 'number' ? `${value}px` : value;
}

export function PrimaryButton({
  title,
  width,
  height,
  onPressed,
}: PrimaryButtonProps) {
  const disabled = onPressed == null;
  const style: CSSProperties = {
    ...COLOR_STYLE,
    ...(width === undefined ? {} : { width: cssSize(width) }),
    ...(height === undefined ? {} : { height: cssSize(height) }),
  };
  return (
    <div className={styles.root} style={style}>
      <button
        type="button"
        className={styles.button}
        disabled={disabled}
        onClick={() => onPressed?.()}
      >
        <span className={styles.label}>{title}</span>
      </button>
    </div>
  );
}
